import {Observable, PartialObserver, Subscription} from 'rxjs';

interface SquashOperator {

    // Spread and not grouped.
    <T>({closingNotifier, filterBy}: {
        closingNotifier: Observable<any>;
        filterBy?: (value: T) => boolean;
        spread?: true
    }): (source: Observable<T>) => Observable<T>;

    // Grouped, not spread.
    <T>({closingNotifier, filterBy, groupBy}: {
        closingNotifier: Observable<any>;
        filterBy?: (value: T) => boolean;
        groupBy: (value: T) => string | number;
        spread?: false
    }): (source: Observable<T>) => Observable<T[]>;

    // Spread.
    <T>({closingNotifier, filterBy, groupBy, spread}: {
        closingNotifier: Observable<any>;
        filterBy?: (value: T) => boolean;
        groupBy?: (value: T) => string | number;
        spread: true;
    }): (source: Observable<T>) => Observable<T>;

    // General definition.
    <T>({closingNotifier, filterBy, groupBy, spread}: {
        closingNotifier: Observable<any>;
        filterBy?: (value: T) => boolean;
        groupBy?: (value: T) => string | number;
        spread?: boolean;
    }): (source: Observable<T>) => Observable<T | T[]>;
}

/**
 * RxJs pipeable operator.
 * Periodically emits the most recent source values.
 * @param {Observable<*>} closingNotifier defines time period boundaries (triggers output and resets the buffer).
 * @param {(value: T) => boolean} filterBy filters input and output values.
 * @param {(value: T) => any} groupBy allows to not squash values of different type.
 * @param {boolean} spread if true, then each output value will be emitted separately, otherwise - as an array at once.
 * @returns {(source: Observable<T>) => Observable<T | T[]>}
 */
export const squash: SquashOperator = <T>({closingNotifier, filterBy, groupBy, spread}: {
    closingNotifier: Observable<any>;
    filterBy?: (value: T) => boolean;
    groupBy?: (value: T) => string | number;
    spread?: boolean;
}): (source: Observable<T>) => Observable<T | T[]> => (source: Observable<T>) =>
    new Observable<T | T[]>(observer => {

        interface Group {
            value: any;
            index: number;
        }

        spread = spread === null || spread === undefined ? !groupBy : spread;
        groupBy = groupBy || (() => 0);
        filterBy = filterBy || (() => true);

        let groups: {[groupKey: string]: Group} = {};
        let index: number = 0;

        const subscription = new Subscription();
        const commonHandlers: PartialObserver<any> = {
            error:    error => observer.error(error),
            complete: () => observer.complete()
        };
        const emit: (values: T[]) => void = spread
            ? values => values.forEach(value => observer.next(value))
            : values => {
                if (values.length) {
                    observer.next(values);
                }
            };

        subscription.add(source.subscribe({
            ...commonHandlers,
            next: value => {
                if (filterBy(value)) {
                    const groupKey = groupBy(value);

                    index++;
                    groups[groupKey] = {value, index};
                }
            }
        }));

        subscription.add(closingNotifier.subscribe({
            ...commonHandlers,
            next: () => {
                const values = Object.values(groups)
                    .sort((group1: Group, group2: Group) => group1.index - group2.index)
                    .map((group: Group) => group.value)
                    .filter(filterBy);

                emit(values);
                groups = {};
                index = 0;
            }
        }));

        return subscription;
    });
